/**
 * Helper functions for visual menu section components.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.5.0
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2022-05-25 (1.0.0) (jhoward)
 *    - Refactor computed and method functions from Romanza_wineList.vue to menu-sections.js helper.
 *
 *  2023-03-27 (1.0.1) (jhoward)
 *    - Fix bug in computeSectionSubtitle() where ms_title was referenced rather than ms_subtitle.
 *
 *  2023-04-17 (1.1.0) (jhoward)
 *    - methodPricePerType() allows a defaultValue to be passed.
 *    - menu-sections.js helper file refactored to menu-sections.ts.
 *
 *  2023-06-19 (1.2.0) (jhoward)
 *    - Add named capture groups for "price" and "denomination" to priceRegex.
 *    - Support inclusion or exclusion of denomination in methodPricePerType().
 *    - Code improvements in menu-sections.ts.
 *
 *  2023-06-21 (1.2.1) (jhoward)
 *    - Do not include the blank pricingRegex type in getPricingTypes().
 *
 *  2023-07-26 (1.2.2) (jhoward)
 *    - Add "1/2 Bottle" regular expression to priceRegex.
 *    - Update all regular expressions in priceRegex to do a negative look behind for "/\d" to support "1/2 Bottle" regex.
 *    - Add group names to binRegex regular expression.
 *
 *  2023-08-28 (1.3.0) (jhoward)
 *    - Add computedIsScheduled() to determine if a menu item, section, or page is scheduled.
 *
 *  2023-09-08 (1.3.1) (jhoward)
 *    - Check new MenuSection.ms_show_bin flag in showBIN().
 *
 *  2023-10-05 (1.4.0) (jhoward)
 *    - Add "Carafe" regular expression to priceRegex.
 *    - computeItemGroupPricingTypes() now checks if the computed pricing types are a subset of the previous group, and if so, copies the previous groups pricing types.
 *
 *  2024-07-09 (1.4.1) (jhoward)
 *    - Add TextualMenuComponent for type of `this` in several functions in menu-sections.ts.
 *
 *  2024-07-19 (1.4.2) (jhoward)
 *    - Support normalized schedules for textual menus in menu-sections.ts.
 *
 *  2025-02-12 (1.4.2) (jhoward)
 *    - Allows return true in computedIsScheduled when app is in admin view mode in menu-sections.ts.
 *    - Add support for MenuTagLegendComponent component in computedIsScheduled in menu-sections.ts.
 *    - Use new schedule_start_timestamp and schedule_end_timestamp properties in methodGetNormalizedSchedules in menu-sections.ts.
 *
 *  2025-04-08 (1.5.0) (jhoward)
 *    - Set Typescript return types for some functions in menu-sections.ts.
 *    - Remove computeItems() function in menu-sections.ts.
 *    - Remove computeItemGroups() function in menu-sections.ts.
 *    - Remove "visual_" and "textual_" prefixes from class names in menu-sections.ts.
 *    - Use "section.grouped_items" rather than "itemGroups" (which has been removed) in computeItemGroupPricingTypes() in menu-sections.ts.
 */

import {
  ItemGroupPricingTypes,
  ItemGroupShowBIN,
  MenuItem,
  MenuSchedule,
  MenuScheduleNormalized,
  MenuSection,
  MenuTagModel,
  PreviousGroupAndSubgroupTitles,
  VisualMenuComponent,
  TextualMenuComponent,
  MenuDefaultItemComponent,
  VisualMenuPageComponent,
  VisualMenuItemGroupComponent,
  VisualMenuSectionComponent,
  TextualMenuSectionComponent,
  MenuSectionListItemComponent,
  MenuTagLegendComponent,
  MenuSwitcherComponent,
  MenuOptionsComponent,
  ALLOWED_ITEM_COMPONENTS, SearchMenuComponent,
} from "../components/menu/defs";

const priceRegex: { [key: string]: RegExp } = {
  'Glass': /(^|[^\/][0-9]\s+)Glass\s+(?!\d+oz)(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Glass 3oz': /(^|[^\/][0-9]\s+)Glass\s*3oz\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Glass 6oz': /(^|[^\/][0-9]\s+)Glass\s*6oz\s(?<denomination>\$?)(?<price>[0-9.]+)[\s$]/i,
  '6oz': /(^|[^\/][0-9]\s+)6oz\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Carafe': /(^|[^\/][0-9]\s+)carafe\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Half Bottle': /(^|[^\/][0-9]\s+)Half\s+Bottles?\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  '1/2 Bottle': /(^|[^\/][0-9]\s+)1\/2\s+Bottles?\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Bottle': /(^|[^\/][^\/][0-9]\s+)Bottle\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Regular': /(^|[^\/][0-9]\s+)Regular\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  'Machete': /(^|[^\/][0-9]\s+)Machete\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  '1.5L': /(^|[^\/][0-9]\s+)1\.5L\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  '750ml': /(^|[^\/][0-9]\s+)750ml\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  '500ml': /(^|[^\/][0-9]\s+)500ml\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  '375ml': /(^|[^\/][0-9]\s+)375ml\s+(?<denomination>\$?)(?<price>[0-9.]+)/i,
  '': /^(\s*)(?<denomination>\$?)(?<price>[0-9.,\s]+\*?)\s*$/i
};
const binRegex: RegExp = /^\s*(?<bin>[0-9]+)(?<binExtension>\/[0-9]+)?\s+(?<title>.+)$/i;

export function methodGetPricingTypes(menuItems: MenuItem[]): string[] {
  const pricingTypes: { [key: string]: boolean } = {};
  for (let pricingType in priceRegex) {
    if ('' !== pricingType) {
      pricingTypes[pricingType] = false;
    }
  }
  menuItems.forEach(item => {
    if (item.hasOwnProperty('mi_price')) {
      for (let priceType in pricingTypes) {
        if (priceType in priceRegex && !pricingTypes[priceType] && priceRegex[priceType].test(item.mi_price)) {
          pricingTypes[priceType] = true;
        }
      }
    }
  });
  const output = [];
  for (let priceType in pricingTypes) {
    if (pricingTypes[priceType]) {
      output.push(priceType);
    }
  }

  return output;
}

export function methodGetPreviousGroupAndSubgroupTitles(this: VisualMenuItemGroupComponent | TextualMenuSectionComponent, groupTitle: string, subgroupTitle: string): PreviousGroupAndSubgroupTitles | null {
  let lastSubgroupTitle = null;
  let lastGroupTitle = null;
  for (let tempLastGroupTitle in this.itemGroupPricingTypes) {
    for (let tempLastSubgroupTitle in this.itemGroupPricingTypes[tempLastGroupTitle]) {
      if (
        tempLastGroupTitle === groupTitle
        && tempLastSubgroupTitle === subgroupTitle
        && lastGroupTitle
        && lastSubgroupTitle
      ) {
        return {
          lastGroupTitle: lastGroupTitle,
          lastSubgroupTitle: lastSubgroupTitle
        };
      }
      lastGroupTitle = tempLastGroupTitle;
      lastSubgroupTitle = tempLastSubgroupTitle;
    }
  }

  return null;
}

export function methodShowBIN(menuItems: MenuItem[], section: MenuSection): boolean {
  //early out if manually showing the BIN is set to off in CMS
  if (!section.ms_show_bin) {
    return false;
  }

  for (let i = 0, len = menuItems.length; i < len; i++) {
    if (methodGetItemBIN(menuItems[i]).length) {
      return true;
    }
  }

  return false;
}

function groupItems(items: MenuItem[], groupByKey: string) {
  let output: { [key: string | number]: MenuItem[] } = {};

  items.forEach(item => {
    let groupByValue = '';
    if (groupByKey in item) {
      //@ts-ignore
      groupByValue = item[groupByKey];
    }

    if (!output.hasOwnProperty(groupByValue)) {
      output[groupByValue] = [];
    }
    output[groupByValue].push(item);
  });

  return output;
}

// export function computeItems(this: VisualMenuItemGroupComponent): MenuItem[] {
//   let output: MenuItem[] = [];
//   for (let topLevelGroupTitle in this.itemGroups) {
//     for (let subLevelGroupTitle in this.itemGroups[topLevelGroupTitle]) {
//       this.itemGroups[topLevelGroupTitle][subLevelGroupTitle].forEach(menuItem => {
//         output.push(menuItem);
//       });
//     }
//   }
//
//   return output;
// }

/**
 * Group items by item_group_name and item_subgroup_name.
 */
// export function computeItemGroups(this: VisualMenuItemGroupComponent): GroupedMenuItems {
//   if (this.section.hasOwnProperty('grouped_items')) {
//     return this.section.grouped_items;
//   }
//
//   return {};
// }

export function computeSectionTitle(this: VisualMenuItemGroupComponent) {
  if (this.section.hasOwnProperty('ms_title')) {
    return this.section.ms_title.replace(/-/g, '<span class="menu--title_hyphen">-</span>');
  }
  return '';
}

export function computeSectionSubtitle(this: VisualMenuItemGroupComponent) {
  if (this.section.hasOwnProperty('ms_subtitle')) {
    return this.section.ms_subtitle.replace(/-/g, '<span class="menu--title_hyphen">-</span>');
  }
  return '';
}

export function computeItemGroupPricingTypes(this: VisualMenuItemGroupComponent | TextualMenuSectionComponent): ItemGroupPricingTypes {
  let output: ItemGroupPricingTypes = {};

  let lastGroupTitle = '';
  let lastSubgroupTitle = '';
  for (let groupTitle in this.section.grouped_items) {
    output[groupTitle] = {};
    for (let subgroupTitle in this.section.grouped_items[groupTitle]) {
      if ('ms_group_by_price' in this.section && this.section.ms_group_by_price) {
        output[groupTitle][subgroupTitle] = [];
        this.section.grouped_items[groupTitle][subgroupTitle].forEach(menuItem => {
          if (-1 === output[groupTitle][subgroupTitle].indexOf(menuItem.mi_price)) {
            output[groupTitle][subgroupTitle].push(menuItem.mi_price);
          }
        });
      } else {
        output[groupTitle][subgroupTitle] = methodGetPricingTypes(this.section.grouped_items[groupTitle][subgroupTitle]);

        //if the computed pricing types are a subset of the previous group, copy the previous groups pricing types
        //  this helps keep the grid of pricing types consistent and clean
        if (
          lastGroupTitle in output
          && lastSubgroupTitle in output[lastGroupTitle]
          && isItemSubgroupPricingASubset(output[groupTitle][subgroupTitle], output[lastGroupTitle][lastSubgroupTitle])
        ) {
          output[groupTitle][subgroupTitle] = output[lastGroupTitle][lastSubgroupTitle];
        }
      }
      lastSubgroupTitle = subgroupTitle;
    }
    lastGroupTitle = groupTitle;
  }

  return output;
}

export function computeItemGroupShowBIN(this: VisualMenuItemGroupComponent): ItemGroupShowBIN {
  let output: ItemGroupShowBIN = {};

  for (let groupTitle in this.section.grouped_items) {
    output[groupTitle] = {};
    for (let subgroupTitle in this.section.grouped_items[groupTitle]) {
      output[groupTitle][subgroupTitle] = methodShowBIN(this.section.grouped_items[groupTitle][subgroupTitle], this.section);
    }
  }

  return output;
}

// export function computeMenuItemComponent(this: VisualMenuSectionComponent) {
//   if ('' !== this.defaultMenuItemComponentOverride && -1 !== ALLOWED_ITEM_COMPONENTS.indexOf(this.defaultMenuItemComponentOverride)) {
//     return this.defaultMenuItemComponentOverride;
//   }
//
//   return 'default-item';
// }

export function methodIsSubgroupPricingShown(this: VisualMenuItemGroupComponent, groupTitle: string, subgroupTitle: string): boolean {
  //get the last group's and subgroup's title
  const lastGroupAndSubgroupTitles = this.getPreviousGroupAndSubgroupTitles(groupTitle, subgroupTitle);
  //check to see if the last subgroup is showing the exact same pricing titles
  return !(
    lastGroupAndSubgroupTitles
    && isItemSubgroupPricingASubset(this.itemGroupPricingTypes[groupTitle][subgroupTitle], this.itemGroupPricingTypes[lastGroupAndSubgroupTitles.lastGroupTitle][lastGroupAndSubgroupTitles.lastSubgroupTitle])
  );
}

function isItemSubgroupPricingASubset(pricingGroup1: string[], pricingGroup2: string[]): boolean {
  return pricingGroup1.length <= pricingGroup2.length && pricingGroup1.every((value, index) => {
    return -1 !== pricingGroup2.indexOf(value);
  });
}

export function methodGetItemBIN(menuItem: MenuItem): string {
  if (menuItem.hasOwnProperty('mi_title')) {
    const match = binRegex.exec(menuItem.mi_title);
    if (match && match.groups) {
      return match.groups.bin + (match.groups.binExtension || '');
    }
  }

  return '';
}

export function getPricePerType(
  priceDisplay: string,
  priceType?: string,
  defaultValue?: string | number,
  removeDenomination: Boolean = false
) {
  if ('string' === typeof priceType && priceRegex.hasOwnProperty(priceType)) {
    const match = priceRegex[priceType].exec(priceDisplay);
    if (match && match.groups) {
      if (removeDenomination) {
        return match.groups.price;
      } else {
        return match.groups.denomination + match.groups.price;
      }
    }
  }

  if ('undefined' === typeof defaultValue) {
    return '';
  }

  return defaultValue;
}

export function methodTagClasses(tag: MenuTagModel) {
  let output: { [key: string]: boolean } = {};

  if (tag.hasOwnProperty('mt_icon_class') && '' !== tag.mt_icon_class) {
    output[tag.mt_icon_class] = true;
  }

  return output;
}

export function methodRemoveBINFromTitle(menuItem: MenuItem) {
  if ('mi_title' in menuItem) {
    const match = binRegex.exec(menuItem.mi_title);
    if (match && match.groups) {
      return match.groups.title;
    }

    return menuItem.mi_title;
  }

  return '';
}

export function computedIsScheduled(this: MenuDefaultItemComponent | VisualMenuComponent | TextualMenuComponent | VisualMenuSectionComponent | TextualMenuSectionComponent | VisualMenuPageComponent | MenuSectionListItemComponent | MenuTagLegendComponent): boolean {
  if (this.$store.state.isAdminView || (this.menu && this.$store.state.optionAllowListHiddenMenuIds.includes(this.menu.menu_id))) {
    return true;
  }

  let normalizedSchedules: MenuScheduleNormalized[] = [];
  if ('item' in this) {
    normalizedSchedules.push(...this.item?.normalizedSchedules || [])
  } else if ('section' in this) {
    normalizedSchedules.push(...this.section.normalizedSchedules || []);
  } else if ('page' in this) {
    normalizedSchedules = normalizedSchedules.concat(...this.page.map(section => section.normalizedSchedules || []));
  } else if ('menu' in this) {
    normalizedSchedules.push(...this.menu?.normalizedSchedules || [])
  }
  if (normalizedSchedules.length > 0) {
    for (let i = 0, l = normalizedSchedules.length; i < l; i++) {
      if (isNormalizedScheduleActive(normalizedSchedules[i], this.$store.state.currentDateTimeUtc, this.$store.state.currentDayOfWeek, this.$store.state.currentTimeOfDay)) {
        return true;
      }
    }
  }

  return !normalizedSchedules.length;
}

export function methodIsModelScheduled(this: MenuDefaultItemComponent | VisualMenuComponent | TextualMenuComponent | VisualMenuSectionComponent | MenuSwitcherComponent | MenuOptionsComponent | SearchMenuComponent, normalizedSchedules: MenuScheduleNormalized[]): boolean {
  if (normalizedSchedules.length > 0) {
    for (let i = 0, l = normalizedSchedules.length; i < l; i++) {
      if (isNormalizedScheduleActive(normalizedSchedules[i], this.$store.state.currentDateTimeUtc, this.$store.state.currentDayOfWeek, this.$store.state.currentTimeOfDay)) {
        return true;
      }
    }
  }

  return !normalizedSchedules.length;
}

export function isNormalizedScheduleActive(normalizedSchedule: MenuScheduleNormalized, currentDateTimeUtc: number, currentDayOfWeek: number, currentTimeOfDay: number): boolean {
  return (
    //check day of week
    (0 === normalizedSchedule.schedule_days_of_week.length || normalizedSchedule.schedule_days_of_week.includes(currentDayOfWeek))

    //check day start time if days of week are set
    && (0 === normalizedSchedule.schedule_days_of_week.length || currentTimeOfDay >= (normalizedSchedule.schedule_day_start_time || 0))

    //check day end time if days of week are set
    && (0 === normalizedSchedule.schedule_days_of_week.length || currentTimeOfDay < (normalizedSchedule.schedule_day_end_time || 2400))

    //check start datetime
    && (currentDateTimeUtc >= normalizedSchedule.schedule_start_timestamp)

    //check end datetime
    && (currentDateTimeUtc < normalizedSchedule.schedule_end_timestamp)
  );
}

export function methodGetNormalizedSchedules(schedules: MenuSchedule[]): MenuScheduleNormalized[] {
  return schedules.map(schedule => {
    const normalizedSchedule: MenuScheduleNormalized = JSON.parse(JSON.stringify(schedule));
    if (schedule.schedule_start_datetime) {
      normalizedSchedule.schedule_start_timestamp = getDateAsUTC(new Date(schedule.schedule_start_datetime || '')).getTime();
    } else {
      normalizedSchedule.schedule_start_timestamp = 0;
    }

    if (schedule.schedule_end_datetime) {
      normalizedSchedule.schedule_end_timestamp = getDateAsUTC(new Date(schedule.schedule_end_datetime || '')).getTime();
    } else {
      normalizedSchedule.schedule_end_timestamp = Number.MAX_SAFE_INTEGER;
    }

    if (!schedule.schedule_day_start_time) {
      normalizedSchedule.schedule_day_start_time = 0;
    }

    if (!schedule.schedule_day_end_time) {
      normalizedSchedule.schedule_day_end_time = 2400;
    }

    return normalizedSchedule;
  });
}

function getSecondsBetweenTwoDates(startDate: Date, endDate: Date): number {
  const millisecondsPerSecond = 1000;

  return Math.round((getDateAsUTC(endDate).getTime() - getDateAsUTC(startDate).getTime()) / millisecondsPerSecond);
}

export function getDateAsUTC(date: Date): Date {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return result;
}